/* Admin Credential Vault — @paranoya'nın özel şifreli kasası
   - Tüm panel kullanıcı/şifre bilgileri Fernet ile şifreli MongoDB'de
   - Reveal eylemleri audit log'a yazılır
   - Sadece admin role erişebilir
*/
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  KeyRound, Plus, Pencil, Trash2, Eye, EyeOff, Copy, Search,
  Server, Coins, Globe, Share2, Wrench, Wallet, Mail, Lock, Box,
  AlertCircle, ShieldCheck, ExternalLink, History,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { http, errMsg } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const vaultApi = {
  list: (params = {}) => http.get("/vault", { params }).then((r) => r.data),
  create: (b) => http.post("/vault", b).then((r) => r.data),
  update: ({ id, ...b }) => http.patch(`/vault/${id}`, b).then((r) => r.data),
  remove: (id) => http.delete(`/vault/${id}`).then((r) => r.data),
  reveal: (id, field) =>
    http.post(`/vault/${id}/reveal`, { field }).then((r) => r.data),
  audit: (limit = 30) =>
    http.get("/vault/audit/log", { params: { limit } }).then((r) => r.data),
};

const TAGS = {
  hosting:  { label: "Hosting",  Icon: Server,      color: "#0A84FF" },
  exchange: { label: "Borsa",    Icon: Coins,       color: "#f0b90b" },
  domain:   { label: "Domain",   Icon: Globe,       color: "#2bd98a" },
  social:   { label: "Sosyal",   Icon: Share2,      color: "#ff5d15" },
  tooling:  { label: "Araçlar",  Icon: Wrench,      color: "#a78bfa" },
  billing:  { label: "Faturalama", Icon: Wallet,    color: "#ec4899" },
  email:    { label: "E-posta",  Icon: Mail,        color: "#22d3ee" },
  vpn:      { label: "VPN/Proxy",Icon: ShieldCheck, color: "#84cc16" },
  other:    { label: "Diğer",    Icon: Box,         color: "#a1a1aa" },
};

const EMPTY = {
  service: "", url: "", username: "", password: "", notes: "",
  client_id: "", totp_secret: "", tag: "other",
};

/* =========================================================================
   FORM
   ========================================================================= */

function VaultForm({ initial, onSave, onCancel, busy }) {
  const [f, setF] = useState({ ...EMPTY, ...(initial || {}) });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const inp =
    "w-full h-9 px-3 rounded bg-[#0a0a0c] border border-white/10 text-sm text-zinc-100 font-mono focus:border-[#ff5d15]/60 outline-none transition-colors";
  const lbl =
    "block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 font-semibold";
  const ta =
    "w-full px-3 py-2 rounded bg-[#0a0a0c] border border-white/10 text-sm text-zinc-100 font-mono focus:border-[#ff5d15]/60 outline-none resize-y min-h-[60px]";

  const submit = () => {
    if (!f.service.trim()) {
      toast.error("Servis adı zorunlu");
      return;
    }
    // password v.b. boş gelirse update durumunda mevcut değer korunsun → trim sadece
    onSave({ ...f, service: f.service.trim() });
  };

  return (
    <div
      className="grid grid-cols-1 md:grid-cols-2 gap-3 p-4 rounded-lg border border-[#ff5d15]/30 bg-[#0a0a0c]/80 backdrop-blur"
      data-testid="vault-form"
    >
      <div className="md:col-span-2 flex items-center gap-2 -mt-1">
        <KeyRound className="w-4 h-4 text-[#ff5d15]" />
        <span className="text-xs uppercase tracking-widest text-[#ff5d15] font-semibold">
          {initial?.id ? "Kayıt Düzenle" : "Yeni Kayıt"}
        </span>
      </div>

      <div>
        <label className={lbl}>Servis Adı *</label>
        <input
          className={inp}
          value={f.service}
          onChange={set("service")}
          placeholder="Hetzner, Binance, GitHub..."
          data-testid="vault-form-service"
        />
      </div>

      <div>
        <label className={lbl}>Etiket</label>
        <select
          className={inp}
          value={f.tag}
          onChange={set("tag")}
          data-testid="vault-form-tag"
        >
          {Object.entries(TAGS).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
      </div>

      <div className="md:col-span-2">
        <label className={lbl}>URL</label>
        <input
          className={inp}
          value={f.url}
          onChange={set("url")}
          placeholder="https://accounts.hetzner.com"
          data-testid="vault-form-url"
        />
      </div>

      <div>
        <label className={lbl}>Kullanıcı Adı / E-posta</label>
        <input
          className={inp}
          value={f.username}
          onChange={set("username")}
          placeholder="meksikalivevo@gmail.com"
          data-testid="vault-form-username"
        />
      </div>

      <div>
        <label className={lbl}>Müşteri/Client No</label>
        <input
          className={inp}
          value={f.client_id}
          onChange={set("client_id")}
          placeholder="K0626737226"
          data-testid="vault-form-client-id"
        />
      </div>

      <div>
        <label className={lbl}>
          Şifre {initial?.id && "(boş = değiştirme)"}
        </label>
        <input
          className={inp}
          value={f.password}
          onChange={set("password")}
          type="password"
          autoComplete="new-password"
          placeholder="••••••••••"
          data-testid="vault-form-password"
        />
      </div>

      <div>
        <label className={lbl}>
          2FA Secret (opsiyonel) {initial?.id && "(boş = değiştirme)"}
        </label>
        <input
          className={inp}
          value={f.totp_secret}
          onChange={set("totp_secret")}
          type="password"
          autoComplete="off"
          placeholder="JBSWY3DPEHPK3PXP"
          data-testid="vault-form-totp"
        />
      </div>

      <div className="md:col-span-2">
        <label className={lbl}>
          Notlar {initial?.id && "(boş = değiştirme)"}
        </label>
        <textarea
          className={ta}
          value={f.notes}
          onChange={set("notes")}
          rows={3}
          placeholder="Recovery code, kurtarma sorusu cevabı, IP whitelist, vb."
          data-testid="vault-form-notes"
        />
      </div>

      <div className="md:col-span-2 flex justify-end gap-2 pt-1">
        <Button
          variant="ghost"
          onClick={onCancel}
          disabled={busy}
          data-testid="vault-form-cancel"
          className="text-zinc-400 hover:text-zinc-200"
        >
          İptal
        </Button>
        <Button
          onClick={submit}
          disabled={busy}
          data-testid="vault-form-save"
          className="bg-[#ff5d15] hover:bg-[#ff5d15]/90 text-black font-semibold"
        >
          {busy ? "Kaydediliyor…" : "Kaydet"}
        </Button>
      </div>
    </div>
  );
}

/* =========================================================================
   ROW (each credential card)
   ========================================================================= */

function VaultRow({ item, onEdit, onDelete }) {
  const tag = TAGS[item.tag] || TAGS.other;
  const Icon = tag.Icon;
  const [revealed, setRevealed] = useState({}); // {password: "plain", ...}

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} kopyalandı`);
    } catch {
      toast.error("Kopyalanamadı");
    }
  };

  const reveal = async (field, label) => {
    if (revealed[field]) {
      // toggle off
      setRevealed((r) => ({ ...r, [field]: undefined }));
      return;
    }
    try {
      const res = await vaultApi.reveal(item.id, field);
      setRevealed((r) => ({ ...r, [field]: res.value }));
    } catch (e) {
      toast.error(errMsg(e, `${label} açılamadı`));
    }
  };

  const revealAndCopy = async (field, label) => {
    try {
      const res = await vaultApi.reveal(item.id, field);
      await navigator.clipboard.writeText(res.value);
      toast.success(`${label} kopyalandı`);
    } catch (e) {
      toast.error(errMsg(e, "Kopyalanamadı"));
    }
  };

  return (
    <div
      className="group relative rounded-lg border border-white/10 bg-[#0a0a0c]/60 hover:border-[#ff5d15]/40 transition-colors p-4"
      data-testid={`vault-row-${item.id}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <div
            className="w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0"
            style={{
              background: `${tag.color}1a`,
              border: `1px solid ${tag.color}44`,
            }}
          >
            <Icon className="w-4 h-4" style={{ color: tag.color }} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h3
                className="text-sm font-semibold text-zinc-100 truncate"
                data-testid={`vault-service-${item.id}`}
              >
                {item.service}
              </h3>
              <span
                className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded font-mono"
                style={{
                  background: `${tag.color}14`,
                  color: tag.color,
                  border: `1px solid ${tag.color}33`,
                }}
              >
                {tag.label}
              </span>
            </div>
            {item.url && (
              <a
                href={item.url.startsWith("http") ? item.url : `https://${item.url}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[11px] text-zinc-500 hover:text-[#ff5d15] inline-flex items-center gap-1 mt-0.5 font-mono"
              >
                {item.url}
                <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={() => onEdit(item)}
            className="p-1.5 rounded hover:bg-white/5 text-zinc-400 hover:text-zinc-100"
            title="Düzenle"
            data-testid={`vault-edit-${item.id}`}
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => onDelete(item)}
            className="p-1.5 rounded hover:bg-[#ff5662]/10 text-zinc-400 hover:text-[#ff5662]"
            title="Sil"
            data-testid={`vault-delete-${item.id}`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Fields */}
      <div className="space-y-2 font-mono text-xs">
        {item.username && (
          <FieldRow
            label="Kullanıcı"
            value={item.username}
            onCopy={() => copy(item.username, "Kullanıcı adı")}
            testIdBase={`vault-${item.id}-username`}
          />
        )}
        {item.has_client_id && (
          <SecretFieldRow
            label="Müşteri No"
            masked={item.client_id_masked}
            revealed={revealed.client_id}
            onToggleReveal={() => reveal("client_id", "Müşteri no")}
            onCopy={() => revealAndCopy("client_id", "Müşteri no")}
            testIdBase={`vault-${item.id}-client-id`}
          />
        )}
        {item.has_password && (
          <SecretFieldRow
            label="Şifre"
            masked={item.password_masked}
            revealed={revealed.password}
            onToggleReveal={() => reveal("password", "Şifre")}
            onCopy={() => revealAndCopy("password", "Şifre")}
            testIdBase={`vault-${item.id}-password`}
          />
        )}
        {item.has_totp && (
          <SecretFieldRow
            label="2FA Seed"
            masked="••••••••"
            revealed={revealed.totp_secret}
            onToggleReveal={() => reveal("totp_secret", "2FA seed")}
            onCopy={() => revealAndCopy("totp_secret", "2FA seed")}
            testIdBase={`vault-${item.id}-totp`}
          />
        )}
        {item.has_notes && (
          <SecretFieldRow
            label="Notlar"
            masked="••• (gizli not)"
            revealed={revealed.notes}
            onToggleReveal={() => reveal("notes", "Notlar")}
            onCopy={() => revealAndCopy("notes", "Notlar")}
            multiline
            testIdBase={`vault-${item.id}-notes`}
          />
        )}
      </div>
    </div>
  );
}

function FieldRow({ label, value, onCopy, testIdBase }) {
  return (
    <div className="flex items-center gap-2 group/row">
      <span className="text-zinc-500 text-[10px] uppercase tracking-wider w-20 flex-shrink-0">
        {label}
      </span>
      <span
        className="text-zinc-200 truncate flex-1"
        data-testid={`${testIdBase}-value`}
      >
        {value}
      </span>
      <button
        onClick={onCopy}
        className="p-1 rounded hover:bg-white/5 text-zinc-500 hover:text-[#ff5d15] opacity-0 group-hover/row:opacity-100 transition-opacity"
        title="Kopyala"
        data-testid={`${testIdBase}-copy`}
      >
        <Copy className="w-3 h-3" />
      </button>
    </div>
  );
}

function SecretFieldRow({
  label, masked, revealed, onToggleReveal, onCopy, multiline, testIdBase,
}) {
  const shown = revealed != null;
  return (
    <div className="flex items-start gap-2 group/row">
      <span className="text-zinc-500 text-[10px] uppercase tracking-wider w-20 flex-shrink-0 pt-0.5">
        {label}
      </span>
      <span
        className={`text-zinc-200 flex-1 break-all ${shown ? "text-[#ff5d15]" : ""}`}
        style={multiline && shown ? { whiteSpace: "pre-wrap" } : {}}
        data-testid={`${testIdBase}-value`}
      >
        {shown ? revealed : masked}
      </span>
      <div className="flex items-center gap-0.5 flex-shrink-0">
        <button
          onClick={onToggleReveal}
          className="p-1 rounded hover:bg-white/5 text-zinc-500 hover:text-[#ff5d15]"
          title={shown ? "Gizle" : "Göster"}
          data-testid={`${testIdBase}-reveal`}
        >
          {shown ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
        </button>
        <button
          onClick={onCopy}
          className="p-1 rounded hover:bg-white/5 text-zinc-500 hover:text-[#ff5d15]"
          title="Kopyala"
          data-testid={`${testIdBase}-copy`}
        >
          <Copy className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

/* =========================================================================
   AUDIT DRAWER
   ========================================================================= */

function AuditPanel({ open, onClose }) {
  const { data: log } = useQuery({
    queryKey: ["vault-audit"],
    queryFn: () => vaultApi.audit(50),
    enabled: open,
  });
  if (!open) return null;
  const actionColor = {
    create: "#2bd98a",
    update: "#0A84FF",
    delete: "#ff5662",
    reveal: "#f0b90b",
  };
  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex justify-end"
      onClick={onClose}
      data-testid="vault-audit-overlay"
    >
      <div
        className="w-full max-w-md h-full bg-[#0a0a0c] border-l border-white/10 overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-[#0a0a0c]/95 backdrop-blur border-b border-white/10 px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <History className="w-4 h-4 text-[#ff5d15]" />
            <h2 className="text-sm font-semibold uppercase tracking-widest">
              Audit Log
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-zinc-500 hover:text-zinc-200 text-xl leading-none"
            data-testid="vault-audit-close"
          >
            ×
          </button>
        </div>
        <div className="p-5 space-y-2">
          {!log?.length && (
            <p className="text-zinc-500 text-sm">Kayıt yok.</p>
          )}
          {log?.map((row) => (
            <div
              key={row.id}
              className="rounded border border-white/10 bg-black/30 p-3 font-mono text-[11px]"
            >
              <div className="flex items-center justify-between">
                <span
                  className="uppercase tracking-wider font-semibold"
                  style={{ color: actionColor[row.action] || "#a1a1aa" }}
                >
                  {row.action}
                </span>
                <span className="text-zinc-500">
                  {new Date(row.at).toLocaleString("tr-TR")}
                </span>
              </div>
              {row.detail && (
                <div className="text-zinc-400 mt-1">{row.detail}</div>
              )}
              <div className="text-zinc-600 mt-1 truncate">
                id: {row.entry_id}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* =========================================================================
   PAGE
   ========================================================================= */

export default function Vault() {
  const { user: me } = useAuth();
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [activeTag, setActiveTag] = useState("all");
  const [editing, setEditing] = useState(null); // null | "new" | {item}
  const [auditOpen, setAuditOpen] = useState(false);

  const { data: items = [], isLoading } = useQuery({
    queryKey: ["vault", activeTag],
    queryFn: () =>
      vaultApi.list(activeTag === "all" ? {} : { tag: activeTag }),
    enabled: me?.role === "admin",
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["vault"] });
    qc.invalidateQueries({ queryKey: ["vault-audit"] });
  };

  const createMut = useMutation({
    mutationFn: vaultApi.create,
    onSuccess: () => {
      toast.success("Kayıt eklendi");
      setEditing(null);
      invalidate();
    },
    onError: (e) =>
      toast.error(errMsg(e, "Kayıt eklenemedi")),
  });

  const updateMut = useMutation({
    mutationFn: vaultApi.update,
    onSuccess: () => {
      toast.success("Güncellendi");
      setEditing(null);
      invalidate();
    },
    onError: (e) =>
      toast.error(errMsg(e, "Güncellenemedi")),
  });

  const deleteMut = useMutation({
    mutationFn: vaultApi.remove,
    onSuccess: () => {
      toast.success("Silindi");
      invalidate();
    },
    onError: (e) =>
      toast.error(errMsg(e, "Silinemedi")),
  });

  const filtered = useMemo(() => {
    if (!search) return items;
    const s = search.toLowerCase();
    return items.filter(
      (it) =>
        (it.service || "").toLowerCase().includes(s) ||
        (it.url || "").toLowerCase().includes(s) ||
        (it.username || "").toLowerCase().includes(s),
    );
  }, [items, search]);

  const tagCounts = useMemo(() => {
    const c = { all: items.length };
    Object.keys(TAGS).forEach((k) => { c[k] = 0; });
    items.forEach((it) => { c[it.tag] = (c[it.tag] || 0) + 1; });
    return c;
  }, [items]);

  if (me?.role !== "admin") {
    return (
      <div className="p-8 max-w-md">
        <div className="bg-[#ff5662]/[0.08] border border-[#ff5662]/40 rounded-md p-4 flex items-start gap-2 text-sm">
          <AlertCircle className="w-4 h-4 text-[#ff5662] mt-0.5" />
          <div>
            <b className="text-[#ff5662]">Yetkin yok.</b>
            <p className="text-zinc-400 mt-1">
              Kasa sadece admin&apos;lere açık.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-5" data-testid="vault-page">
      {/* ===== Hero ===== */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-[#ff5d15]/15 border border-[#ff5d15]/40 flex items-center justify-center">
              <KeyRound className="w-5 h-5 text-[#ff5d15]" />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
                Kasa
                <Lock className="w-4 h-4 text-[#ff5d15]" />
              </h1>
              <p className="text-xs text-zinc-500 font-mono mt-0.5">
                Şifreli kimlik kasası · Fernet AES-128 · sadece sen erişebilirsin
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setAuditOpen(true)}
            data-testid="vault-audit-btn"
            className="border-white/10 text-zinc-300 hover:bg-white/5 hover:text-zinc-100"
          >
            <History className="w-4 h-4 mr-1.5" />
            Audit Log
          </Button>
          <Button
            onClick={() => setEditing("new")}
            data-testid="vault-new-btn"
            className="bg-[#ff5d15] hover:bg-[#ff5d15]/90 text-black font-semibold"
          >
            <Plus className="w-4 h-4 mr-1.5" />
            Yeni Kayıt
          </Button>
        </div>
      </div>

      {/* ===== Security note ===== */}
      <div className="rounded-lg border border-[#ff5d15]/30 bg-[#ff5d15]/[0.04] p-3 flex items-start gap-2 text-xs">
        <ShieldCheck className="w-4 h-4 text-[#ff5d15] mt-0.5 flex-shrink-0" />
        <div className="text-zinc-400 font-mono leading-relaxed">
          Tüm hassas alanlar <b className="text-[#ff5d15]">Fernet (AES-128-CBC + HMAC)</b> ile
          şifrelenir. Şifreler asla düz metin saklanmaz. Her &quot;göster/kopyala&quot;
          eylemi audit log&apos;a yazılır.
        </div>
      </div>

      {/* ===== Search + filters ===== */}
      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Hetzner, Binance, github..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="vault-search"
            className="w-full h-10 pl-10 pr-3 rounded-md bg-[#0a0a0c] border border-white/10 text-sm focus:border-[#ff5d15]/60 outline-none transition-colors text-zinc-200"
          />
        </div>
      </div>

      {/* ===== Tag pills ===== */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="vault-tag-filters">
        <TagPill
          active={activeTag === "all"}
          onClick={() => setActiveTag("all")}
          color="#ff5d15"
          label="Tümü"
          count={tagCounts.all}
          testId="vault-tag-all"
        />
        {Object.entries(TAGS).map(([k, v]) => (
          <TagPill
            key={k}
            active={activeTag === k}
            onClick={() => setActiveTag(k)}
            color={v.color}
            label={v.label}
            Icon={v.Icon}
            count={tagCounts[k] || 0}
            testId={`vault-tag-${k}`}
          />
        ))}
      </div>

      {/* ===== Form (when editing) ===== */}
      {editing === "new" && (
        <VaultForm
          onSave={(data) => createMut.mutate(data)}
          onCancel={() => setEditing(null)}
          busy={createMut.isPending}
        />
      )}
      {editing && editing !== "new" && (
        <VaultForm
          initial={editing}
          onSave={(data) => {
            // sadece dolu alanları gönder (boş şifre → değişme)
            const clean = { id: editing.id };
            ["service", "url", "username", "tag"].forEach((k) => {
              clean[k] = data[k] ?? "";
            });
            ["password", "notes", "client_id", "totp_secret"].forEach((k) => {
              if (data[k] !== "") clean[k] = data[k];
            });
            updateMut.mutate(clean);
          }}
          onCancel={() => setEditing(null)}
          busy={updateMut.isPending}
        />
      )}

      {/* ===== List ===== */}
      <div className="space-y-3">
        {isLoading && (
          <div className="text-center py-10 text-zinc-500 text-sm font-mono">
            Yükleniyor…
          </div>
        )}
        {!isLoading && filtered.length === 0 && (
          <div
            className="text-center py-16 rounded-lg border border-dashed border-white/10"
            data-testid="vault-empty"
          >
            <KeyRound className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-400 text-sm">
              {search || activeTag !== "all"
                ? "Eşleşen kayıt yok."
                : "Henüz hiç kayıt yok."}
            </p>
            {!search && activeTag === "all" && (
              <Button
                onClick={() => setEditing("new")}
                variant="outline"
                className="mt-4 border-[#ff5d15]/40 text-[#ff5d15] hover:bg-[#ff5d15]/10"
              >
                <Plus className="w-4 h-4 mr-1.5" /> İlk kaydı ekle
              </Button>
            )}
          </div>
        )}
        {filtered.map((it) => (
          <VaultRow
            key={it.id}
            item={it}
            onEdit={(item) => setEditing(item)}
            onDelete={(item) => {
              if (
                window.confirm(
                  `"${item.service}" kaydını silmek istediğine emin misin? Bu işlem geri alınamaz.`,
                )
              ) {
                deleteMut.mutate(item.id);
              }
            }}
          />
        ))}
      </div>

      <AuditPanel open={auditOpen} onClose={() => setAuditOpen(false)} />
    </div>
  );
}

function TagPill({ active, onClick, color, label, Icon, count, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-xs font-mono uppercase tracking-wider border transition-colors ${
        active
          ? "border-[#ff5d15]/60 bg-[#ff5d15]/15 text-[#ff5d15]"
          : "border-white/10 bg-transparent text-zinc-400 hover:text-zinc-200 hover:border-white/20"
      }`}
      style={active ? { borderColor: `${color}99`, background: `${color}1a`, color } : {}}
    >
      {Icon && <Icon className="w-3 h-3" />}
      {label}
      {count > 0 && (
        <span className="text-[10px] opacity-70">· {count}</span>
      )}
    </button>
  );
}
